import { createUseStyles } from 'react-jss';
import { Classes, Styles } from 'jss';

import { GrayscalePalette, ActionPalette, BrandPalette } from '@tcl-boron-colors/colors';

import { MenuDirection } from './menuDirection';

// exported so that we can calculate total expanded menu in ./menuPostion.ts
export const ICON_MARGIN = 10;
export const MAX_WIDTH = 320;
export const MIN_WIDTH = 220;
export const ROW_SIDES_PADDING = 16;
export const ROW_TOP_BOTTOM_PADDING = 10;
export const ROW_FONT_SIZE = 14;
export const ROW_HEIGHT = 2 * ROW_TOP_BOTTOM_PADDING + ROW_FONT_SIZE;
export const HORIZONTAL_POSITION_OFFSET = 24;
export const MARGIN_TO_ROOT = 2;
export const MARGIN_TO_BASE_ROOT = 8;
export const CONTAINER_VERTICAL_PADDING = 8;

const getMenuDirection = (direction: MenuDirection): Styles => {
  switch (direction) {
    case 'above-right':
      return {
        top: '0',
        left: '0',
        transform: `translateY(calc(-100% - ${MARGIN_TO_BASE_ROOT}px))`,
      };
    case 'above-left':
      return {
        top: '0',
        right: '0',
        transform: `translateY(calc(-100% - ${MARGIN_TO_BASE_ROOT}px))`,
      };
    case 'below-right':
      return {
        top: `calc(100% + ${MARGIN_TO_BASE_ROOT}px)`,
        left: '0',
      };
    case 'below-left':
      return {
        top: `calc(100% + ${MARGIN_TO_BASE_ROOT}px)`,
        right: '0',
      };
    case 'side-right-down':
      return {
        top: `-${CONTAINER_VERTICAL_PADDING}px`,
        left: `calc(100% + ${MARGIN_TO_ROOT}px)`,
      };
    case 'side-right-up':
      return {
        top: '0',
        left: `calc(100% + ${MARGIN_TO_ROOT}px)`,
        transform: `translateY(calc(-100% + ${ROW_HEIGHT}px + ${CONTAINER_VERTICAL_PADDING}px))`,
      };
    case 'side-left-down':
      return {
        top: `-${CONTAINER_VERTICAL_PADDING}px`,
        transform: `translateX(calc(-100% - ${MARGIN_TO_ROOT}px))`,
      };
    case 'side-left-up':
      return {
        top: '0',
        transform: `translateY(calc(-100% + ${ROW_HEIGHT}px + ${CONTAINER_VERTICAL_PADDING}px)) translateX(calc(-100% - ${MARGIN_TO_ROOT}px))`,
      };
    default:
      return {};
  }
};

const getCaretDirection = (
  menuDirection: Omit<MenuDirection, 'side-right-up' | 'side-right-down' | 'side-left-up' | 'side-left-down'>,
): Styles => {
  switch (menuDirection) {
    case 'above-right':
      return {
        bottom: '-0.5px',
        left: '33.2px',
        transform: 'translate(-50%, 50%) rotateZ(45deg)',
      };
    case 'above-left':
      return {
        bottom: '-0.5px',
        right: '19px',
        transform: 'translate(-50%, 50%) rotateZ(45deg)',
      };
    case 'below-right':
      return {
        top: '-10px',
        left: '33.2px',
        transform: 'translate(-50%, 50%) rotateZ(-135deg)',
      };
    case 'below-left':
      return {
        top: '-10px',
        right: '19px',
        transform: 'translate(-50%, 50%) rotateZ(-135deg)',
      };
    // unreachable
    default:
      return {};
  }
};

export type MenuClasses = Classes<
  | 'root'
  | 'backgroundLayer'
  | 'caret'
  | 'menuDirection'
  | 'menuItemIcon'
  | 'menuItemRowDisabled'
  | 'menuItemRowFocused'
  | 'menuItemRowEnabled'
  | 'menuItemRow'
  | 'menuItemsList'
  | 'menuItemRowContent'
  | 'menuItemsContainer'
  | 'menuItemRowNotFocused'
  | 'menuRootSubMenuOpen'
>;
export const useMenuStyles: (
  data?: { menuDirection: MenuDirection; theme: Jss.Theme },
  theme?: Jss.Theme | undefined,
) => MenuClasses = createUseStyles({
  root: {
    position: 'relative',
    width: '100%',
  },
  backgroundLayer: {
    position: 'fixed',
    top: 0,
    left: 0,
    height: '100vh',
    width: '100vw',
  },
  caret: ({ menuDirection }) => ({
    backgroundColor: 'inherit',
    borderBottom: `1px solid ${GrayscalePalette[300]}`,
    borderRight: `1px solid ${GrayscalePalette[300]}`,
    height: '9.22px',
    position: 'absolute',
    width: '9.22px',
    ...getCaretDirection(menuDirection),
  }),
  menuDirection: ({ menuDirection }) => getMenuDirection(menuDirection),
  menuItemsContainer: {
    padding: `${CONTAINER_VERTICAL_PADDING}px 0px`,
    border: `1px solid ${GrayscalePalette[300]}`,
    borderRadius: '3px',
    position: 'absolute',
    background: 'white',
    zIndex: 999,
  },
  menuItemIcon: {
    color: 'currentcolor',
    marginRight: `${ICON_MARGIN}px`,
  },
  menuItemRowDisabled: {
    color: GrayscalePalette[400],
  },
  menuItemRowFocused: {
    border: `3px solid ${ActionPalette[300]}`,
    borderRadius: '10px',
  },
  menuRootSubMenuOpen: {
    background: ActionPalette[200],
    border: `3px solid ${ActionPalette[200]}`,
  },
  menuItemRowNotFocused: {
    border: `3px solid transparent`,
  },
  menuItemRowEnabled: {
    color: BrandPalette[700],
    cursor: 'pointer',
    transition: 'background 150ms ease-out',
    '&:hover': {
      background: ActionPalette[200],
      border: `3px solid ${ActionPalette[200]}`,
    },
    '&:active': {
      transition: 'background 0ms',
      background: ActionPalette[400],
    },
  },
  menuItemRow: {
    boxSizing: 'border-box',
    minWidth: `${MIN_WIDTH}px`,
    maxWidth: `${MAX_WIDTH}px`,
    overflow: 'clip',
    position: 'relative',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    padding: `${ROW_TOP_BOTTOM_PADDING - 3}px ${ROW_SIDES_PADDING}px`,
    fontSize: `${ROW_FONT_SIZE}px`,
    lineHeight: `${ROW_FONT_SIZE}px`,
  },
  menuItemRowContent: {
    display: 'flex',
    alignItems: 'center',
  },
  menuItemsList: {
    '&>:not(:last-child)': {
      boxSizing: 'border-box',
      borderBottom: `1px solid ${GrayscalePalette[400]}`,
    },
  },
});
